import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   NICU / NEONATAL MODULE — Complete Newborn Care
   - Birth Registration & Apgar Scoring
   - Incubator / Warmer Allocation
   - Weight Monitoring Chart
   - Feeding Record (Breast / Formula / NG)
   - Bilirubin / Phototherapy Tracking
   - CPAP / Ventilator Parameters
   - Neonatal Screening (NBS)
   - Discharge Criteria Checklist
═══════════════════════════════════════════════════════════ */

const APGAR_ITEMS = [
  { criterion: 'Heart Rate', scores: ['Absent (0)', '<100 bpm (1)', '>100 bpm (2)'] },
  { criterion: 'Respiratory Effort', scores: ['Absent (0)', 'Weak/Irregular (1)', 'Crying (2)'] },
  { criterion: 'Muscle Tone', scores: ['Limp (0)', 'Some Flexion (1)', 'Active Motion (2)'] },
  { criterion: 'Reflex Response', scores: ['None (0)', 'Grimace (1)', 'Cry/Cough/Sneeze (2)'] },
  { criterion: 'Colour', scores: ['Blue/Pale (0)', 'Blue Extremities (1)', 'Pink All Over (2)'] },
];

function IncubatorsSetup({ onSave, busy }) {
  const [rows, setRows] = useState([{ id: 'INC-01', type: 'Closed Incubator' }]);
  return (
    <div className="card mb" style={{ borderLeft: '4px solid #10b981' }}>
      <h3 style={{ marginTop: 0, color: '#10b981' }}>Configure Incubators / Warmers</h3>
      <p className="hint mb">No incubators configured. Add units before allocating neonates.</p>
      {rows.map((r, i) => (
        <div key={i} className="form-grid mb">
          <div className="field"><label>Unit ID</label><input className="input" value={r.id} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, id: e.target.value } : x))} /></div>
          <div className="field"><label>Type</label><select className="input" value={r.type} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}><option>Closed Incubator</option><option>Radiant Warmer</option><option>Open Care System</option></select></div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows((list) => [...list, { id: `INC-${String(list.length + 1).padStart(2, '0')}`, type: 'Closed Incubator' }])}>+ Add Unit</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onSave(rows.map((r) => ({ ...r, status: 'Available', baby: null, temp: null, humidity: null })))}>{busy ? 'Saving…' : '✓ Save Units'}</button>
      </div>
    </div>
  );
}

function ApgarCalculator() {
  const [scores, setScores] = useState({ 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 });
  const total = Object.values(scores).reduce((s, v) => s + v, 0);
  const apgarColor = total >= 7 ? '#10b981' : total >= 4 ? '#f59e0b' : '#dc2626';
  const apgarLabel = total >= 7 ? 'Normal' : total >= 4 ? 'Requires Assistance' : 'Immediate Resuscitation';

  return (
    <div className="card" style={{ borderLeft: '4px solid #6366f1' }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>📊 Apgar Score Calculator</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {APGAR_ITEMS.map((item, idx) => (
          <div key={idx} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>{item.criterion}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {item.scores.map((label, val) => (
                <button key={val} type="button" onClick={() => setScores(s => ({ ...s, [idx]: val }))} style={{ padding: '4px 12px', borderRadius: 8, border: '2px solid', borderColor: scores[idx] === val ? '#6366f1' : '#e2e8f0', background: scores[idx] === val ? '#6366f1' : '#fff', color: scores[idx] === val ? '#fff' : '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: '16px 20px', background: `${apgarColor}15`, border: `2px solid ${apgarColor}40`, borderRadius: 14, display: 'flex', gap: 20, alignItems: 'center' }}>
        <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 48, fontWeight: 900, color: apgarColor, lineHeight: 1 }}>{total}</div>
        <div>
          <div style={{ fontWeight: 900, fontSize: 16, color: apgarColor }}>{apgarLabel}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Score: {total}/10 · {total >= 7 ? 'No intervention needed' : total >= 4 ? 'Stimulation + Supplemental O₂' : '⚠ Positive pressure ventilation required'}</div>
        </div>
      </div>
    </div>
  );
}

function WeightChartTab({ neonate, onSaveWeights }) {
  const [weights, setWeights] = useState(neonate?.weights || [
    { day: 'Birth', date: neonate?.dob || '', weight: neonate?.birthWeightGrams || 2450, feed: 'Nil', urine: false, stool: false },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (neonate?.weights?.length) setWeights(neonate.weights);
    else if (neonate) {
      setWeights([{ day: 'Birth', date: neonate.dob || '', weight: neonate.birthWeightGrams || 2450, feed: 'Nil', urine: false, stool: false }]);
    }
  }, [neonate]);
  const birthWeight = weights[0]?.weight || 2450;
  const latestWeight = weights[weights.length - 1]?.weight || 2450;
  const weightChange = latestWeight - birthWeight;
  const pctChange = ((weightChange / birthWeight) * 100).toFixed(1);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 12 }}>
        {[{ label: 'Birth Weight', val: `${birthWeight}g`, color: '#6366f1' }, { label: 'Current Weight', val: `${latestWeight}g`, color: '#10b981' }, { label: 'Weight Change', val: `${weightChange > 0 ? '+' : ''}${weightChange}g (${pctChange}%)`, color: weightChange >= 0 ? '#10b981' : '#f97316' }, { label: 'Days', val: weights.length - 1, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 16, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>📈 Weight & Feeding Chart</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setWeights(w => [...w, { day: `Day ${w.length}`, date: '', weight: latestWeight, feed: '', urine: false, stool: false }])}>+ Add Reading</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={saving || !onSaveWeights} onClick={async () => {
            if (!onSaveWeights) return;
            setSaving(true);
            try { await onSaveWeights(weights); } finally { setSaving(false); }
          }}>{saving ? 'Saving…' : '✓ Save Chart'}</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Day</th><th>Weight (g)</th><th>Change</th><th>Feeding</th><th>Urine</th><th>Stool</th><th>Bilirubin</th></tr></thead>
            <tbody>
              {weights.map((w, i) => {
                const prev = weights[i - 1]?.weight;
                const change = prev ? w.weight - prev : 0;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{w.day}</td>
                    <td style={{ fontWeight: 900, fontFamily: 'Outfit,sans-serif', color: '#6366f1', fontSize: 14 }}>{w.weight}g</td>
                    <td style={{ fontWeight: 700, color: i === 0 ? '#94a3b8' : change >= 0 ? '#10b981' : '#f97316' }}>{i === 0 ? 'Baseline' : `${change > 0 ? '+' : ''}${change}g`}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}><input className="input" style={{ width: 140, padding: '2px 8px', fontSize: 11 }} value={w.feed} onChange={e => setWeights(arr => arr.map((x, j) => j === i ? { ...x, feed: e.target.value } : x))} /></td>
                    <td style={{ textAlign: 'center', fontSize: 16 }}>{w.urine ? '💧' : '—'}</td>
                    <td style={{ textAlign: 'center', fontSize: 16 }}>{w.stool ? '✓' : '—'}</td>
                    <td><input type="number" step="0.1" className="input" style={{ width: 70, padding: '2px 8px', fontSize: 11 }} placeholder="mg/dL" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function NicuPage() {
  const { records: neonates, patch, loading } = useHmsRecords('nicu-neonate');
  const { config, save: saveIncubators, loading: incLoading } = useHmsConfig('nicu-incubators');
  const [tab, setTab] = useState('census');
  const [selectedNeonate, setSelectedNeonate] = useState(null);
  const [savingInc, setSavingInc] = useState(false);

  const incubators = config?.incubators || [];

  useEffect(() => {
    if (neonates.length && !selectedNeonate) setSelectedNeonate(neonates[0]);
  }, [neonates, selectedNeonate]);

  const saveWeights = async (weights) => {
    if (!selectedNeonate) return;
    await patch(selectedNeonate.recordId || selectedNeonate.id, { weights });
  };

  const neoList = neonates.length ? neonates : [];

  const handleSaveIncubators = async (list) => {
    setSavingInc(true);
    try {
      await saveIncubators({ incubators: list });
    } finally { setSavingInc(false); }
  };

  return (
    <PageShell title="NICU — Neonatal Intensive Care Unit" subtitle="Birth Registration · Apgar Score · Weight Chart · Incubator Management · Feeding Record">
      {!incLoading && incubators.length === 0 && (
        <IncubatorsSetup onSave={handleSaveIncubators} busy={savingInc} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Neonates', val: neoList.length, color: '#6366f1' }, { label: 'Incubators In Use', val: incubators.filter(i => i.status === 'In Use').length, color: '#f59e0b' }, { label: 'Available', val: incubators.filter(i => i.status === 'Available').length, color: '#10b981' }, { label: 'On CPAP', val: neoList.filter((n) => n.onCpap).length, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'census', label: '👶 NICU Census' }, { id: 'apgar', label: '📊 Apgar Calculator' }, { id: 'weight', label: '📈 Weight Chart' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'census' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 12 }}>
          {neoList.length === 0 && !loading && <p className="hint table-empty full">No neonates admitted. Register a birth from Labour &amp; Delivery or add a NICU admission.</p>}
          {neoList.map(neo => (
            <div key={neo.id || neo.recordId} className="card" style={{ borderLeft: '4px solid #6366f1', cursor: 'pointer', border: selectedNeonate?.id === neo.id || selectedNeonate?.recordId === neo.recordId ? '2px solid #6366f1' : undefined }} onClick={() => setSelectedNeonate(neo)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 900, fontSize: 15 }}>👶 {neo.babyName}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: neo.gestationWeeks < 35 ? '#fef2f2' : '#fffbeb', color: neo.gestationWeeks < 35 ? '#dc2626' : '#92400e', border: `1px solid ${neo.gestationWeeks < 35 ? '#fca5a5' : '#fde68a'}` }}>{neo.gestationWeeks} wks</span>
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>Mother: <strong>{neo.motherName}</strong> ({neo.motherUhid})</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8, fontSize: 12 }}>
                {[['DOB/TOB', `${neo.dob} ${neo.tob}`], ['Gender', neo.gender === 'M' ? '♂ Male' : '♀ Female'], ['Birth Weight', `${neo.birthWeightGrams}g`], ['Delivery', neo.deliveryType], ['Apgar 1/5', `${neo.apgar1min}/${neo.apgar5min}`], ['Incubator', neo.incubator]].map(([label, val]) => (
                  <div key={label} style={{ padding: '4px 8px', background: '#f8fafc', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{label}</div>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Reason: {neo.admitReason}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Paed: {neo.paediatrician} · Obs: {neo.obstetrician}</div>
            </div>
          ))}
          <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
            <h4 style={{ marginTop: 0, color: '#10b981' }}>🌡 Incubator / Warmer Status</h4>
            {incubators.length === 0 ? (
              <p className="hint">Configure incubators above.</p>
            ) : incubators.map(inc => (
              <div key={inc.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: inc.status === 'In Use' ? '#fffbeb' : '#f0fdf4', border: `1px solid ${inc.status === 'In Use' ? '#fde68a' : '#86efac'}`, marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{inc.id} — {inc.type}</div>
                  {inc.baby && <div style={{ fontSize: 11, color: '#64748b' }}>Baby: {inc.baby} · {inc.temp}°C · {inc.humidity}% RH</div>}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: inc.status === 'In Use' ? '#fef9c3' : '#dcfce7', color: inc.status === 'In Use' ? '#92400e' : '#16a34a' }}>{inc.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'apgar' && <ApgarCalculator />}
      {tab === 'weight' && <WeightChartTab neonate={selectedNeonate} onSaveWeights={saveWeights} />}
    </PageShell>
  );
}
